import { useQuery } from '@tanstack/react-query';
import dataService from 'services/dataService';
import { apiPatientAverageMetrics } from 'services/dataService/resources';

import { showResult } from '../../utils';

import type { IAveragesResponse } from 'infrastructure/interfaces';

interface IGetAverages {
  patientGuid: string;
  endTime?: number;
  timezoneOffset: number;
}

const getAveragesFn = async ({
  patientGuid,
  timezoneOffset,
  endTime,
}: IGetAverages) => {
  const { data, error } = await dataService.getList<IAveragesResponse>(
    apiPatientAverageMetrics(patientGuid, {
      endTime,
      timezoneOffset,
    }),
  );

  if (error) {
    showResult(error);
    throw new Error(error);
  }

  return data;
};

interface Props {
  patientGuid: string;
  enable?: boolean;
  endTime?: number;
}

export const getAveragesKey = (filter: IGetAverages) => ['getAverages', filter];

export const useGetAverages = ({
  patientGuid,
  enable = true,
  endTime,
}: Props) => {
  const timezoneOffset = new Date().getTimezoneOffset();

  const { data: averages, isLoading } = useQuery({
    queryKey: getAveragesKey({
      patientGuid,
      timezoneOffset,
      endTime,
    }),
    queryFn: async () =>
      getAveragesFn({
        patientGuid,
        timezoneOffset,
        endTime,
      }),
    staleTime: 5 * 1000 * 60,
    enabled: Boolean(endTime) && enable,
  });

  return {
    averages,
    isLoading,
  };
};
